import { apiFetch } from '@/lib/http/client';
import type {
  SubscriptionStatus,
  CreditBalance,
  CheckoutSessionResponse,
  PortalSessionResponse,
  CancelResponse,
  ResumeResponse,
  BillingInterval,
  PlanPricesResponse,
  PlanCatalogResponse,
  ReferralResponse,
  ValidatePromoResponse,
} from './types';

// apiFetch prepends `/api`; paths here are unprefixed. Workspace context rides
// on the X-Workspace-ID header injected by lib/http/client — never sent in body.
/** Init that pins a request to a specific workspace, overriding the active one. */
function scoped(workspaceId: string | undefined, init: RequestInit = {}): RequestInit {
  return workspaceId
    ? { ...init, headers: { ...(init.headers as Record<string, string>), 'X-Workspace-ID': workspaceId } }
    : init;
}

export function getSubscription(workspaceId?: string): Promise<SubscriptionStatus> {
  return apiFetch<SubscriptionStatus>('/billing/subscription', scoped(workspaceId));
}

export function getCreditBalance(workspaceId?: string): Promise<CreditBalance> {
  return apiFetch<CreditBalance>('/billing/credits', scoped(workspaceId));
}

export function getPlanPrices(planCode = 'pro'): Promise<PlanPricesResponse> {
  return apiFetch<PlanPricesResponse>(
    `/billing/plan-prices?plan=${encodeURIComponent(planCode)}`,
  );
}

export function getPlanCatalog(
  opts: { planCode?: string; workspaceType?: 'personal' | 'team' } = {},
): Promise<PlanCatalogResponse> {
  const params = new URLSearchParams();
  if (opts.planCode) params.set('plan', opts.planCode);
  else if (opts.workspaceType) params.set('workspaceType', opts.workspaceType);
  const q = params.toString();
  return apiFetch<PlanCatalogResponse>(`/billing/plans${q ? `?${q}` : ''}`);
}

export interface ChangePlanResponse {
  planCode: string;
  cancelAtPeriodEnd: boolean;
}

export function changePlan(
  planCode: string,
  interval?: BillingInterval,
  workspaceId?: string,
): Promise<ChangePlanResponse> {
  return apiFetch<ChangePlanResponse>(
    '/billing/change-plan',
    scoped(workspaceId, { method: 'POST', body: JSON.stringify({ planCode, interval }) }),
  );
}

export interface ChangePlanPreview {
  planCode: string;
  amountDueNowCents: number | null;
  currency: string | null;
  recurringAmountCents: number | null;
}

export function previewChangePlan(
  planCode: string,
  interval?: BillingInterval,
  workspaceId?: string,
): Promise<ChangePlanPreview> {
  return apiFetch<ChangePlanPreview>(
    '/billing/change-plan/preview',
    scoped(workspaceId, { method: 'POST', body: JSON.stringify({ planCode, interval }) }),
  );
}

export function createCheckoutSession(
  planCode = 'pro',
  interval: BillingInterval = 'month',
  workspaceId?: string,
  promotionCode?: string,
): Promise<CheckoutSessionResponse> {
  return apiFetch<CheckoutSessionResponse>(
    '/billing/checkout-session',
    scoped(workspaceId, {
      method: 'POST',
      body: JSON.stringify(
        promotionCode ? { planCode, interval, promotionCode } : { planCode, interval },
      ),
    }),
  );
}

export function createPortalSession(workspaceId?: string): Promise<PortalSessionResponse> {
  return apiFetch<PortalSessionResponse>(
    '/billing/portal-session',
    scoped(workspaceId, { method: 'POST', body: JSON.stringify({}) }),
  );
}

export function cancelSubscription(workspaceId?: string): Promise<CancelResponse> {
  return apiFetch<CancelResponse>('/billing/cancel', scoped(workspaceId, {
    method: 'POST',
    body: JSON.stringify({}),
  }));
}

export function resumeSubscription(workspaceId?: string): Promise<ResumeResponse> {
  return apiFetch<ResumeResponse>('/billing/resume', scoped(workspaceId, {
    method: 'POST',
    body: JSON.stringify({}),
  }));
}

export function getReferral(): Promise<ReferralResponse> {
  return apiFetch<ReferralResponse>('/billing/referral');
}

export function validatePromoCode(
  code: string,
  interval: BillingInterval = 'month',
  workspaceId?: string,
): Promise<ValidatePromoResponse> {
  return apiFetch<ValidatePromoResponse>(
    '/billing/validate-promo',
    scoped(workspaceId, { method: 'POST', body: JSON.stringify({ code, interval }) }),
  );
}
